import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetBands, apiGetTeam, apiGetShows, apiGetVenues, apiGetContacts,
  apiGetCalendar, apiGetSetlists, apiGetChecklists, apiGetRider, apiGetConfig, apiGetMusicians,
  apiGetCrew, apiGetStagePlots, apiGetItinerary, apiGetSongs,
  apiGetDaysheetLayoutEffective, apiGetPdfLayoutEffective, todayMinDate,
  type BandDTO,
} from '../api';
import { preloadUpcomingShows } from '../lib/autoPreload';
import { subscribeOffline, type OfflineState } from '../lib/offline';
import PageHeader from '../components/PageHeader';

interface PreloadJob {
  id: string;
  label: string;
  run: () => Promise<unknown>;
}

export default function PreloadOfflinePage() {
  const { t } = useTranslation();
  const [bands, setBands] = useState<BandDTO[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [log, setLog] = useState<string[]>([]);
  const [offline, setOffline] = useState<OfflineState>({ offline: false, pendingCount: 0 });
  const [finished, setFinished] = useState<{ ok: number; total: number } | null>(null);
  // PWA install prompt (beforeinstallprompt fires when the page is installable).
  const [installEvt, setInstallEvt] = useState<any | null>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  async function onInstall() {
    if (!installEvt) return;
    setInstalling(true);
    try {
      installEvt.prompt();
      await installEvt.userChoice;
    } finally {
      setInstallEvt(null);
      setInstalling(false);
    }
  }

  useEffect(() => {
    const unsub = subscribeOffline(setOffline);
    return unsub;
  }, []);

  useEffect(() => {
    apiGetBands()
      .then((b) => setBands(b))
      .catch(() => setBands([]));
  }, []);

  const push = useCallback((msg: string) => {
    setLog((prev) => [...prev.slice(-80), msg]);
  }, []);

  const jobs: PreloadJob[] = useMemo(() => {
    const global: PreloadJob[] = [
      { id: 'team', label: 'Team (people)', run: () => apiGetTeam() },
      { id: 'shows', label: 'Shows', run: () => apiGetShows() },
      { id: 'venues', label: 'Venues', run: () => apiGetVenues() },
      { id: 'contacts', label: 'Contacts', run: () => apiGetContacts() },
      { id: 'calendar', label: 'Calendar', run: () => apiGetCalendar() },
      {
        id: 'daysheets',
        label: 'Upcoming Day Sheets (roadmaps, hotels, flights, rooming, notes)',
        run: async () => {
          const shows = await apiGetShows({ minDate: todayMinDate() });
          // Up to 20 closest upcoming dates — the ones a travelling user opens.
          await preloadUpcomingShows(shows, 20);
          return shows;
        },
      },
    ];
    const perBand: PreloadJob[] = bands.flatMap((b) => [
      { id: `band:${b.slug}:rider`, label: `Rider (${b.name})`, run: () => apiGetRider(b.id) },
      { id: `band:${b.slug}:config`, label: `Config (${b.name})`, run: () => apiGetConfig(b.id) },
      { id: `band:${b.slug}:musicians`, label: `Musicians (${b.name})`, run: () => apiGetMusicians(b.id) },
      { id: `band:${b.slug}:crew`, label: `Crew (${b.name})`, run: () => apiGetCrew(b.id) },
      { id: `band:${b.slug}:stageplots`, label: `Stage plots (${b.name})`, run: () => apiGetStagePlots(b.id) },
      { id: `band:${b.slug}:setlists`, label: `Setlists (${b.name})`, run: () => apiGetSetlists(b.id) },
      { id: `band:${b.slug}:songs`, label: `Songs (Songbook) (${b.name})`, run: () => apiGetSongs(b.id) },
      { id: `band:${b.slug}:checklists`, label: `Checklists (${b.name})`, run: () => apiGetChecklists(b.id) },
      { id: `band:${b.slug}:itinerary`, label: `Itinerary (${b.name})`, run: () => apiGetItinerary(b.id) },
      {
        id: `band:${b.slug}:layouts`,
        label: `Daysheet & PDF layouts (${b.name})`,
        run: () => Promise.all([
          apiGetDaysheetLayoutEffective(b.id),
          apiGetPdfLayoutEffective(b.id),
          apiGetTeam({ band: b.id }),
        ]),
      },
    ]);
    return [...global, ...perBand];
  }, [bands]);

  const preload = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setFinished(null);
    setDone(0);
    setTotal(jobs.length);
    setLog([]);
    push(t('preload.preloadingResources', { count: jobs.length }));
    // Sequential to keep the log readable and avoid hammering the server.
    let ok = 0;
    for (const job of jobs) {
      try {
        await job.run();
        ok += 1;
        push(`✓ ${job.label}`);
      } catch (err) {
        push(`✗ ${job.label} — ${err instanceof Error ? err.message : String(err)}`);
      }
      setDone((d) => d + 1);
    }
    push(t('preload.doneCached', { ok, total: jobs.length }));
    setFinished({ ok, total: jobs.length });
    setRunning(false);
  }, [jobs, running, push]);

  return (
    <div className="page">
      <PageHeader
        title={t('preload.title')}
        actions={
          <>
            {installEvt && (
              <button className="btn btn-secondary" onClick={onInstall} disabled={installing}>
                {installing ? t('preload.installing') : t('preload.installApp')}
              </button>
            )}
            <button className="btn btn-primary" onClick={preload} disabled={running || offline.offline}>
              {running ? t('preload.preloading') : t('preload.preloadEverything')}
            </button>
          </>
        }
      />

      {offline.offline ? (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'color-mix(in srgb, var(--danger) 12%, transparent)' }}>
          <p style={{ margin: 0, color: 'var(--text-dim)' }}>
            {t('preload.offlineWarn')}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <p style={{ margin: 0, color: 'var(--text-dim)' }}>
            {t('preload.preloadHint')} {offline.pendingCount > 0 && (
              <strong style={{ color: 'var(--text-dim)' }}>
                {t(offline.pendingCount > 1 ? 'preload.waitingToSync_other' : 'preload.waitingToSync', { count: offline.pendingCount })}
              </strong>
            )}
          </p>
        </div>
      )}

      {finished && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 16,
            background: finished.ok === finished.total
              ? 'color-mix(in srgb, var(--success) 14%, transparent)'
              : 'color-mix(in srgb, var(--danger) 12%, transparent)',
            border: `1px solid ${finished.ok === finished.total ? 'var(--success)' : 'var(--danger)'}`,
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>
            {finished.ok === finished.total
              ? t('preload.complete', { ok: finished.ok, total: finished.total })
              : t('preload.finishedFailures', { failures: finished.total - finished.ok, ok: finished.ok, total: finished.total })}
          </p>
          {finished.ok === finished.total && (
            <div style={{ marginTop: 10, fontSize: 'var(--font-sm)', color: 'var(--text-dim)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>{t('preload.offlineProcedure')}</p>
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                <li>{t('preload.procInstall')}</li>
                <li>{t('preload.procGoOffline')}</li>
                <li>{t('preload.procEnterData')}</li>
                <li>{t('preload.procBackOnline')}</li>
                <li>{t('preload.procNeedNow')}</li>
              </ol>
            </div>
          )}
        </div>
      )}

      {total > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-dim)' }}>{t('preload.progress')}</span>
            <span style={{ color: 'var(--text-dim)' }}>{done}/{total}</span>
          </div>
          <div style={{ background: 'color-mix(in srgb, var(--text-muted) 15%, transparent)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
            <div
              style={{
                background: 'var(--accent)',
                height: '100%',
                width: `${total ? (done / total) * 100 : 0}%`,
                transition: 'width .2s ease',
              }}
            />
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ marginTop: 0 }}>{t('preload.whatGetsCached', { count: jobs.length })}</h2>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-dim)' }}>
          {jobs.map((j) => (
            <li key={j.id}>{j.label}</li>
          ))}
        </ul>
      </div>

      {log.length > 0 && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <h2 style={{ marginTop: 0 }}>{t('preload.log')}</h2>
          <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto', fontSize: 'var(--font-xs)', margin: 0 }}>
            {log.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}